import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchApplicationStatusAndRedirectIfNotExpectedState } from '../applicationStatusHelpers';
import { ApplicationStatusModel } from '../../serverGeneratedTypings/applicationStatusModel';
import { N2MajorApplicationStatus } from '../../serverGeneratedTypings/n2MajorApplicationStatus';
import { pollForExpectedApplicationState, setPpi } from '../../services/legacyApplicationApiService';
import { Button, ButtonRowContainer, CheckBox, Headline, Hero, RadioButton, useDismissableErrorMessage } from '../../ui';
import style from './style.module.scss';
import { useApplicationIdContext } from '../../components/ApplicationContext';
import { N2ApplicationStatus } from '../../serverGeneratedTypings/n2ApplicationStatus';
import { ApplicationPaths, navigateToStep } from '../ApplicationFormsHelpers';
import { trackSectionView } from '../../lib/gtm';
import { SubmitProgressBox } from '@n2-common/ui/SubmitProgress';
import { useTranslation } from 'react-i18next';
import { SubmitState } from '@n2-common/components/MultiStageOnePageWizard/types';
import { CommonStatusPageProps } from '@n2-common/components/ApplicationStatusPage';
import { logErrorToServerside } from '@n2-common/services/serverSideLogging';
import { errorToAction } from '../applicationErrorHelper';

export function applicationIsInValidStateForInsurance(applicationStatus: ApplicationStatusModel) {
    if (applicationStatus.MajorStatus !== N2MajorApplicationStatus.Aproved) {
        return false;
    }
    if (!applicationStatus.IsMainApplicant) {
        return false;
    }
    if (!applicationStatus.EligiblePPI) {
        return false;
    }
    return true;
}

type InsuranceProps  = {
    areYouSureYouDoNotWantPpiBox: JSX.Element;
}


export function AreYouSureYouDoNotWantPpiBox(): JSX.Element {
    const { country } = useApplicationIdContext();
    switch (country) {
        case 'FI':
            return (
                <Hero ui='secondary' className={style.areYouSureNotBox}>
                    <p>
                        Pyydämme sinua harkitsemaan päätöstäsi uudelleen!
                        </p><br/>
                        <p>
                        Lainaturvavakuutus on turva sinulle ja läheisillesi sairastumisen, työttömyyden tai menehtymisen sattuessa.
                        Kuolemantapauksessa vakuutus korvaa kokonaissaldon jopa 50&nbsp;000 euroon asti.
                    </p>
                    <br/>
                    <p>Huomioithan, että vakuutusta ei voi ostaa jälkeenpäin!</p>
                </Hero>
            );
        case 'NO':
            return (
                <Hero ui='secondary' className={style.areYouSureNotBox}>
                    <p>
                        Vi ber deg vurdere en gang til om du ønsker en betalingsforsikring. Betalingsforsikringen kan dekke ditt månedlige avdrag ved
                        sykdom og arbeidsledighet/permittering. Dersom du skulle dø kan lånet bli nedbetalt av betalingsforsikringen med inntil kr 500&nbsp;000,-
                    </p><br/>
                    <p>Vi gjør oppmerksom på at det bare mulig å kjøpe forsikringen når du tar opp et lån.</p>
                </Hero>
            );
        default:
            return <></>;
    }
}

type CombinedInsuranceProps = InsuranceProps &CommonStatusPageProps & React.PropsWithChildren;

function InsurancePage({ children, navigateToCorrectStepFunc, areYouSureYouDoNotWantPpiBox }: CombinedInsuranceProps): JSX.Element {
    const navigate = useNavigate();
    const { setErrorMessage, ErrorOutput } = useDismissableErrorMessage();
    const { t } = useTranslation('common');
    const [submitState, setSubmitState] = useState<SubmitState>('NotSent');

    const { applicationId, applicantId, country, productType } = useApplicationIdContext();

    const [applicationStatus, setApplicationStatus] = useState<ApplicationStatusModel | null>(null);
    const [wantsPpi, setWantsPpi] = useState<boolean | null>(null);
    const [agressToTerms, setAgressToTerms] = useState<boolean>(false);
    const [showError, setShowError] = useState(false);
    const [showMustAccept, setShowMustAccept] = useState(false);

    useEffect(() => {
        document.title = t<string>('Ppi.Title');
    }, [t]);

    useEffect(() => {
        fetchApplicationStatusAndRedirectIfNotExpectedState(
            applicationId,
            applicantId,
            applicationIsInValidStateForInsurance,
            setApplicationStatus,
            navigate,
            navigateToCorrectStepFunc
        );
    }, [applicationId, navigate, applicantId, navigateToCorrectStepFunc]);

    useEffect(() => {
        trackSectionView(productType, country, '3.3', 'PPI');
    }, [country, productType]);

    const onSubmit = async () => {
        if (wantsPpi === null || !applicationStatus) {
            setShowError(true);
            setSubmitState('Error');
            return;
        }
        if (wantsPpi && !agressToTerms) {
            setShowMustAccept(true);
            setSubmitState('Error');
            return;
        }
        try {
            setSubmitState('Submitted');
            await setPpi(wantsPpi, applicationId, applicantId);

            setSubmitState('Processing');
            await pollForExpectedApplicationState(
                applicationId,
                applicantId,
                (res: ApplicationStatusModel) => res.Status === N2ApplicationStatus.ReadyForSigning
            );
        } catch (err) {
            const action = errorToAction(err, onSubmit, applicationId, applicantId);
            if (action) {
                setErrorMessage(action);
                return;
            } else {
                // What happened here?
                logErrorToServerside('Unhandled error when submitting PPI', applicationId, applicantId, err);
                navigateToStep(ApplicationPaths.error, applicationId, navigate, applicantId);
            }
        }
        navigateToStep(ApplicationPaths.sign, applicationId, navigate, applicantId);
    };

    const onChangeFunc = (val: boolean) => {
        setWantsPpi(val);
        setShowError(false);
    };

    const onChangeAgreesToTermFunc = (val: boolean) => {
        setAgressToTerms(val);
        setShowMustAccept(false);
    };

    return (
        <>
            {applicationId && applicationStatus ? (
                <div className={style.wrapper}>
                    <SubmitProgressBox
                        state={submitState}
                        submitText={t<string>('Ppi.Submitted')}
                        processingText={t<string>('Ppi.Processing')}
                    ></SubmitProgressBox>
                    <Headline>{t<string>('Ppi.Headline')}</Headline>

                    <Hero className={style.offerBox}>
                        <h1 className={style.headline}></h1>
                        <fieldset>
                            <legend>{t<string>('Ppi.Legend')}</legend>
                            <RadioButton
                                name="ppi"
                                id="ppi_yes"
                                onChange={(e) => onChangeFunc(e.target.value === 'on')}
                                label={t<string>('Ppi.YesLabel')}
                            ></RadioButton>
                            {wantsPpi && (
                                <CheckBox
                                    error={showMustAccept ? t<string>('Ppi.AcceptTerms') : false}
                                    name="agreeppiterms"
                                    id="agreeppiterms"
                                    onChange={(e) => onChangeAgreesToTermFunc(e.target.checked)}
                                    label={t<string>('Ppi.AgreementCheckboxLabel')}
                                ></CheckBox>
                            )}
                            <RadioButton
                                error={showError ? t<string>('Ppi.PleaseSelect') : false}
                                name="ppi"
                                id="ppi_no"
                                onChange={(e) => onChangeFunc(e.target.value !== 'on')}
                                label={t<string>('Ppi.NoLabel')}
                            ></RadioButton>
                        </fieldset>
                    </Hero>
                    {wantsPpi === false && areYouSureYouDoNotWantPpiBox}
                    <ButtonRowContainer>
                       {(wantsPpi === true || wantsPpi === false) && 
                         <Button className={style.nextButton} uiType="primary" onClick={() => onSubmit()}>
                            {t<string>('Ppi.SubmitLabel')}
                        </Button>}
                    </ButtonRowContainer>
                    {children}
                </div>
            ) : (
                <></>
            )}
            <ErrorOutput />
        </>
    );
}

export default InsurancePage;
